import type { DiffDescription } from 'typedefs/DiffDescription';
import { StyleRegion } from './StyleRegion';

/** Describes one side of a code diff */
export type CodeDiff = {
	/** Whether this is the left or right source of the compare. */
	side: 'left' | 'right';
	description: DiffDescription;
	/**
	 * The regions which have been changed in the last commit as inlined-pairs of start-offset and end-offset of the
	 * changed character regions.
	 */
	inLastCommitChangedRegions: number[] | null;
};

/** Applies the change regions to the oldStyleRegions by splitting them up if necessary */
export function applyDiffChangeRegions(oldStyleRegions: StyleRegion[], diff: CodeDiff): StyleRegion[] {
	let styleRegions = oldStyleRegions;
	if (diff.side === 'left') {
		styleRegions = applyChangeRegions(styleRegions, diff.description.leftChangeRegions);
	} else {
		styleRegions = applyChangeRegions(styleRegions, diff.description.rightChangeRegions);
	}
	if (diff.inLastCommitChangedRegions !== null) {
		styleRegions = applyChangeRegions(styleRegions, diff.inLastCommitChangedRegions, true);
	}
	return styleRegions;
}

/**
 * Merges the change regions into the styleRegions array so the highlighted regions are visible. Note that token end
 * index is inclusive while region end index is exclusive.
 *
 * @param highlightInYellow Indicates if the highlight color should be the default one or yellow
 */
function applyChangeRegions(
	oldStyleRegions: StyleRegion[],
	changedRegions: number[],
	highlightInYellow = false
): StyleRegion[] {
	let classSuffix;
	if (!highlightInYellow) {
		classSuffix = ' diff-highlight';
	} else {
		classSuffix = ' diff-highlight-secondary';
	}

	// Add empty token at end, to ensure we do not need range checking below
	oldStyleRegions.push(createDummyEntry());
	const styleRegions: StyleRegion[] = [];
	let tokenIndex = 0;
	for (let regionIndex = 0; regionIndex < changedRegions.length; regionIndex += 2) {
		const regionStart = changedRegions[regionIndex]!;
		const regionEnd = changedRegions[regionIndex + 1]!;
		tokenIndex = advanceIntoRegion(oldStyleRegions, styleRegions, regionStart, tokenIndex);
		tokenIndex = completeRegion(oldStyleRegions, styleRegions, regionEnd, tokenIndex, classSuffix);
	}
	while (tokenIndex < oldStyleRegions.length - 1) {
		styleRegions.push(oldStyleRegions[tokenIndex]!);
		tokenIndex += 1;
	}
	return styleRegions;
}

/** Creates a dummy style region entry. */
function createDummyEntry(): StyleRegion {
	return new StyleRegion(1e9, 1e9, '0', StyleRegion.NO_STYLE);
}

/**
 * Advances the regionIndex into the current region and inserts the styleRegions encountered so far into the
 * formatterContext. Afterward, we can assume that start of the oldStyleRegions at the regionIndex is equal to our
 * regionStart.
 *
 * @param regionStart Start offset of the current region
 * @param regionIndex The current style-region index
 * @returns The new style-region index
 */
function advanceIntoRegion(
	oldStyleRegions: StyleRegion[],
	styleRegions: StyleRegion[],
	regionStart: number,
	regionIndex: number
): number {
	while (oldStyleRegions[regionIndex]!.endOffset < regionStart) {
		styleRegions.push(oldStyleRegions[regionIndex]!);
		regionIndex += 1;
	}
	if (oldStyleRegions[regionIndex]!.startOffset < regionStart) {
		styleRegions.push(
			new StyleRegion(
				oldStyleRegions[regionIndex]!.startOffset,
				regionStart - 1,
				oldStyleRegions[regionIndex]!.styleCssClassName,
				oldStyleRegions[regionIndex]!.tokenStyleClass
			)
		);
		oldStyleRegions[regionIndex]!.startOffset = regionStart;
	}
	return regionIndex;
}

/**
 * Advances the token index to the end of the current region and copies the tokens encountered so far into the tokenData
 * array, modifying the style used (highlighting).
 *
 * @param regionStart Start offset of the current region
 * @param regionEnd End offset of the current region
 * @param regionIndex The current style-region index
 * @param cssClassSuffix The css class name to append
 * @returns The new style-region index
 */
function completeRegion(
	oldStyleRegion: StyleRegion[],
	styleRegions: StyleRegion[],
	regionEnd: number,
	regionIndex: number,
	cssClassSuffix: string
): number {
	while (oldStyleRegion[regionIndex]!.startOffset < regionEnd) {
		const styleCssClassName = oldStyleRegion[regionIndex]!.styleCssClassName + ' ' + cssClassSuffix;
		if (oldStyleRegion[regionIndex]!.endOffset < regionEnd) {
			styleRegions.push(
				new StyleRegion(
					oldStyleRegion[regionIndex]!.startOffset,
					oldStyleRegion[regionIndex]!.endOffset,
					styleCssClassName,
					oldStyleRegion[regionIndex]!.tokenStyleClass
				)
			);
			regionIndex += 1;
		} else {
			styleRegions.push(
				new StyleRegion(
					oldStyleRegion[regionIndex]!.startOffset,
					regionEnd - 1,
					styleCssClassName,
					oldStyleRegion[regionIndex]!.tokenStyleClass
				)
			);
			oldStyleRegion[regionIndex]!.startOffset = regionEnd;
		}
	}
	return regionIndex;
}

/**
 * Determines the color to be used for a given changed line. The colors are computed for one file (shown left or right
 * in the compare view).
 *
 * @param hasChanges Whether the current file (left or right) has changes to the line
 * @param hasOtherChanges Whether the other file (left or right) has changes to the line
 */
function determineLineColor(hasChanges: boolean, hasOtherChanges: boolean, side: 'left' | 'right'): string {
	if (hasChanges === hasOtherChanges) {
		return 'blue';
	} else if (hasChanges) {
		if (side === 'left') {
			return 'red';
		}
		return 'green';
	} else if (side === 'left') {
		return 'green';
	}
	return 'red';
}

/**
 * Determines for each line whether it is a line that has been added/removed/changed and whether the line is the first
 * or last line within such a region. The returned map maps from line numbers to the CSS class names that represent the
 * corresponding type visually (diff-marked-xxx/diff-lines-start-xxx/diff-lines-end-xxx)
 */
export function getLineChangeTypes(diff: CodeDiff | undefined): Map<number, string> {
	const changeTypes = new Map<number, string>();
	if (diff === undefined) {
		return changeTypes;
	}
	let changedLines;
	let otherChangedLines;
	if (diff.side === 'left') {
		changedLines = diff.description.leftChangeLines;
		otherChangedLines = diff.description.rightChangeLines;
	} else {
		changedLines = diff.description.rightChangeLines;
		otherChangedLines = diff.description.leftChangeLines;
	}
	for (let i = 0; i < changedLines.length; i += 2) {
		let start = changedLines[i]!;
		const end = changedLines[i + 1]!;
		if (start < 0) {
			start = 0;
		}
		const hasChanges = changedLines[i]! < changedLines[i + 1]!;
		const hasOtherChanges = otherChangedLines[i]! < otherChangedLines[i + 1]!;
		const color = determineLineColor(hasChanges, hasOtherChanges, diff.side);

		for (let j = start; j <= end; ++j) {
			changeTypes.set(j, 'diff-marked-' + color);
		}
		changeTypes.set(start, (changeTypes.get(start) ?? '') + ' diff-lines-start');

		if (end > 0 && end > start) {
			changeTypes.set(end - 1, (changeTypes.get(end - 1) ?? '') + ' diff-lines-end');
		}
	}
	return changeTypes;
}
